import React, { useState, useMemo, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faReceipt,
  faMoneyBill,
  faFilter,
  faEye,
  faSpinner,
  faCheckCircle,
  faClose,
  faBalanceScale,
  faTimes,
  faGlobe,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line no-unused-vars
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Authorization: authHeader,
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Authorization: authHeader,
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function ViewSalesRegister() {
  const { stateNavigator } = useNavigationEvent();

  const userId = ls.get("currentuserid");

  // Column-specific filters
  const [columnFilters, setColumnFilters] = useState({
    invoiceNumber: "",
    invoiceDate: "",
    customerName: "",
    customerContact: "",
    cashier: "",
    totalAmount: "",
    amountPaid: "",
    balance: "",
    dateRangeStart: "",
    dateRangeEnd: "",
    orderType: "", // 'all', 'pos', 'online'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const paymentOptions = [
    { value: "cash", label: "Cash", icon: faMoneyBill },
    { value: "credit_card", label: "Credit Card" },
    { value: "debit_card", label: "Debit Card" },
    { value: "mobile_money", label: "Mobile Money" },
    { value: "cheque", label: "Cheque" },
  ];

  const orderTypeOptions = [
    { value: "", label: "All Orders" },
    { value: "pos", label: "POS Orders" },
    { value: "online", label: "Online Orders" },
  ];

  // SWR Data fetching for Sales
  const { data: salessales, mutate: mutatesales } = useSWR(
    `${process.env.REACT_APP_SALES_ORDER_API}?include=field_cashier,field_customer&sort=-created&page[limit]=1000`,
    fetcher,
  );
  console.log("Sales Data:", salessales);

  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_customer_email,field_phone_number,field_customer_address,drupal_internal__nid&page[limit]=1000`,
    fetcher,
  );
  console.log("Customers Data NOW:", customers);

  const { data: users } = useSWR(
    `${process.env.REACT_APP_USER_API}?&filter[drupal_internal__uid]=${userId}`,
    fetcher,
  );

  // Helper functions
  const getCashierName = (sale) => {
    const cashierId = sale.relationships?.field_cashier?.data?.id;
    if (!cashierId || !users?.data) return "N/A";
    const user = users.data.find((u) => u.id === cashierId);
    return user?.attributes?.name || "N/A";
  };

  const getCustomerName = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.title || "N/A";
  };

  const getCustomerPhoneNumber = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.field_phone_number || "N/A";
  };

  const calculateSaleBalance = (sale) => {
    const total = parseFloat(sale.attributes.field_total_amount || 0);
    const paid = parseFloat(sale.attributes.field_total_paid || 0);
    return total - paid;
  };

  const getBalanceDisplay = (sale) => {
    const balance = calculateSaleBalance(sale);

    if (balance > 0) {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#d32f2f",
        status: "Due",
        value: balance,
      };
    } else if (balance < 0) {
      return {
        formatted: `GH₵${Math.abs(balance).toFixed(2)}`,
        color: "#ff9800",
        status: "Credit",
        value: balance,
      };
    } else {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#388e3c",
        status: "Paid",
        value: balance,
      };
    }
  };

  // Check if sale is online order
  const isOnlineOrder = (sale) => {
    return sale.attributes.field_online_order === true || 
           sale.attributes.field_online_order === "1" ||
           sale.attributes.field_payment_type === "online";
  };

  // Filter sales based on column filters
  const filteredsales = useMemo(() => {
    if (!salessales?.data) return [];

    return salessales.data.filter((sale) => {
      // Order Type filter
      if (columnFilters.orderType) {
        const isOnline = isOnlineOrder(sale);
        if (columnFilters.orderType === "pos" && isOnline) return false;
        if (columnFilters.orderType === "online" && !isOnline) return false;
      }

      // Invoice Number filter
      if (columnFilters.invoiceNumber) {
        const invoiceNum =
          sale.attributes.field_order_number?.toLowerCase() || "";
        if (!invoiceNum.includes(columnFilters.invoiceNumber.toLowerCase())) {
          return false;
        }
      }

      // Invoice Date filter
      if (columnFilters.invoiceDate) {
        const saleDate = new Date(sale.attributes.created).toLocaleDateString();
        if (!saleDate.includes(columnFilters.invoiceDate)) {
          return false;
        }
      }

      // Customer Name filter
      if (columnFilters.customerName) {
        const customerName = getCustomerName(sale).toLowerCase();
        if (!customerName.includes(columnFilters.customerName.toLowerCase())) {
          return false;
        }
      }

      // Customer Contact filter
      if (columnFilters.customerContact) {
        const customerContact = getCustomerPhoneNumber(sale).toLowerCase();
        if (
          !customerContact.includes(columnFilters.customerContact.toLowerCase())
        ) {
          return false;
        }
      }

      // Cashier filter
      if (columnFilters.cashier) {
        const cashierName = getCashierName(sale).toLowerCase();
        if (!cashierName.includes(columnFilters.cashier.toLowerCase())) {
          return false;
        }
      }

      // Total Amount filter
      if (columnFilters.totalAmount) {
        const totalAmount = parseFloat(sale.attributes.field_total_amount || 0);
        const filterValue = parseFloat(columnFilters.totalAmount);
        if (Math.abs(totalAmount - filterValue) > 0.01) {
          return false;
        }
      }

      // Amount Paid filter
      if (columnFilters.amountPaid) {
        const amountPaid = parseFloat(sale.attributes.field_total_paid || 0);
        const filterValue = parseFloat(columnFilters.amountPaid);
        if (Math.abs(amountPaid - filterValue) > 0.01) {
          return false;
        }
      }

      // Balance filter
      if (columnFilters.balance) {
        const balance = calculateSaleBalance(sale);
        const filterValue = parseFloat(columnFilters.balance);
        if (Math.abs(balance - filterValue) > 0.01) {
          return false;
        }
      }

      // Date range filter - Start Date
      if (columnFilters.dateRangeStart) {
        const saleDate = new Date(sale.attributes.created)
          .toISOString()
          .split("T")[0];
        if (saleDate < columnFilters.dateRangeStart) return false;
      }

      // Date range filter - End Date
      if (columnFilters.dateRangeEnd) {
        const saleDate = new Date(sale.attributes.created)
          .toISOString()
          .split("T")[0];
        if (saleDate > columnFilters.dateRangeEnd) return false;
      }

      return true;
    });
  }, [salessales, columnFilters]);

  // Calculate statistics based on filtered sales (NOT all data)
  const statistics = useMemo(() => {
    const sales = filteredsales;
    
    if (!sales || sales.length === 0) {
      return {
        totalRevenue: 0,
        totalsales: 0,
        averagesale: 0,
        totalBalanceDue: 0,
        totalAmountPaid: 0,
        posRevenue: 0,
        posSales: 0,
        onlineRevenue: 0,
        onlineSales: 0,
      };
    }

    let totalRevenue = 0;
    let totalAmountPaid = 0;
    let posRevenue = 0;
    let posSales = 0;
    let onlineRevenue = 0;
    let onlineSales = 0;

    sales.forEach((sale) => {
      const amount = parseFloat(sale.attributes.field_total_amount || 0);
      const paid = parseFloat(sale.attributes.field_total_paid || 0);
      const isOnline = isOnlineOrder(sale);

      totalRevenue += amount;
      totalAmountPaid += paid;

      if (isOnline) {
        onlineRevenue += amount;
        onlineSales += 1;
      } else {
        posRevenue += amount;
        posSales += 1;
      }
    });

    const totalsales = sales.length;
    const averagesale = totalsales > 0 ? totalRevenue / totalsales : 0;
    const totalBalanceDue = totalRevenue - totalAmountPaid;

    return {
      totalRevenue,
      totalsales,
      averagesale,
      totalBalanceDue,
      totalAmountPaid,
      posRevenue,
      posSales,
      onlineRevenue,
      onlineSales,
    };
  }, [filteredsales]);

  const { 
    totalRevenue, 
    totalsales, 
    totalBalanceDue,
    posRevenue,
    posSales,
    onlineRevenue,
    onlineSales,
  } = statistics;

  // Pagination
  const totalPages = Math.ceil(filteredsales.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSales = filteredsales.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters, itemsPerPage]);

  const handleDelete = async (salesId) => {
    try {
      const freshToken = await getCsrfToken();

      if (!freshToken) {
        setErrorMessage("Unable to authenticate. Please refresh the page.");
        setModalError(true);
        return;
      }

      if (window.confirm("Are you sure you want to delete this sale?")) {
        const res = await fetch(
          `${process.env.REACT_APP_SALES_ORDER_API}/${salesId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": freshToken,
            },
            credentials: "include",
          },
        );

        if (res.ok) {
          setModalSuccessMsg(true);
          mutatesales();
        } else {
          setErrorMessage("Failed to delete sale");
          setModalError(true);
        }
      }
    } catch (error) {
      setErrorMessage("Network error");
      setModalError(true);
    }
  };

  const handleFilterChange = (field, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearAllFilters = () => {
    setColumnFilters({
      invoiceNumber: "",
      invoiceDate: "",
      customerName: "",
      customerContact: "",
      cashier: "",
      totalAmount: "",
      amountPaid: "",
      balance: "",
      dateRangeStart: "",
      dateRangeEnd: "",
      orderType: "",
    });
  };

  const hasActiveFilters = () => {
    return Object.values(columnFilters).some((filter) => filter !== "");
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="sales-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Sales Register"
            buttonLabel="Add Sales"
            onClick={() => stateNavigator.navigate("salesorder")}
          />

          {/* STATISTICS - Updated based on filters */}
          <div style={styles.statisticsGrid}>
            {/* Total Sales Card */}
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faReceipt} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{totalsales}</h3>
                <p style={styles.statLabel}>Total Sales</p>
                <small style={styles.statSubtext}>
                  GH₵{totalRevenue.toFixed(2)}
                </small>
              </div>
            </div>

            {/* POS Sales Card */}
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faStore} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{posSales}</h3>
                <p style={styles.statLabel}>POS Sales</p>
                <small style={styles.statSubtext}>
                  GH₵{posRevenue.toFixed(2)}
                </small>
              </div>
            </div>

            {/* Online Sales Card */}
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faGlobe} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{onlineSales}</h3>
                <p style={styles.statLabel}>Online Sales</p>
                <small style={styles.statSubtext}>
                  GH₵{onlineRevenue.toFixed(2)}
                </small>
              </div>
            </div>

            {/* Balance Due Card */}
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faBalanceScale} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{totalBalanceDue.toFixed(2)}</h3>
                <p style={styles.statLabel}>Total Balance Due</p>
              </div>
            </div>
          </div>

          {/* COLUMN FILTERS SECTION - 4 columns per row */}
          {showFilters && (
            <div style={styles.filtersContainer}>
              <div style={styles.filtersGrid}>
                {/* Row 1: Order Type, Invoice Number, Invoice Date, Customer Name */}
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Order Type</label>
                  <select
                    style={styles.filterInput}
                    value={columnFilters.orderType}
                    onChange={(e) =>
                      handleFilterChange("orderType", e.target.value)
                    }
                  >
                    {orderTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Invoice Number</label>
                  <input
                    type="text"
                    style={styles.filterInput}
                    placeholder="Search invoice..."
                    value={columnFilters.invoiceNumber}
                    onChange={(e) =>
                      handleFilterChange("invoiceNumber", e.target.value)
                    }
                  />
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Invoice Date</label>
                  <input
                    type="text"
                    style={styles.filterInput}
                    placeholder="Search date..."
                    value={columnFilters.invoiceDate}
                    onChange={(e) =>
                      handleFilterChange("invoiceDate", e.target.value)
                    }
                  />
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Customer Name</label>
                  <input
                    type="text"
                    style={styles.filterInput}
                    placeholder="Search customer..."
                    value={columnFilters.customerName}
                    onChange={(e) =>
                      handleFilterChange("customerName", e.target.value)
                    }
                  />
                </div>

                {/* Row 2: Customer Contact, Cashier, Total Amount, Amount Paid */}
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Customer Contact</label>
                  <input
                    type="text"
                    style={styles.filterInput}
                    placeholder="Search phone..."
                    value={columnFilters.customerContact}
                    onChange={(e) =>
                      handleFilterChange("customerContact", e.target.value)
                    }
                  />
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Cashier</label>
                  <input
                    type="text"
                    style={styles.filterInput}
                    placeholder="Search cashier..."
                    value={columnFilters.cashier}
                    onChange={(e) =>
                      handleFilterChange("cashier", e.target.value)
                    }
                  />
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    style={styles.filterInput}
                    placeholder="Exact amount..."
                    value={columnFilters.totalAmount}
                    onChange={(e) =>
                      handleFilterChange("totalAmount", e.target.value)
                    }
                  />
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Amount Paid</label>
                  <input
                    type="number"
                    step="0.01"
                    style={styles.filterInput}
                    placeholder="Exact amount..."
                    value={columnFilters.amountPaid}
                    onChange={(e) =>
                      handleFilterChange("amountPaid", e.target.value)
                    }
                  />
                </div>

                {/* Row 3: Date Range Start, Date Range End */}
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>Start Date</label>
                  <input
                    type="date"
                    style={styles.filterInput}
                    value={columnFilters.dateRangeStart}
                    onChange={(e) =>
                      handleFilterChange("dateRangeStart", e.target.value)
                    }
                  />
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>End Date</label>
                  <input
                    type="date"
                    style={styles.filterInput}
                    value={columnFilters.dateRangeEnd}
                    onChange={(e) =>
                      handleFilterChange("dateRangeEnd", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* SALE LIST TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Invoice Date</th>
                    <th>Customer Name</th>
                    <th>Customer Contact</th>
                    <th>Cashier</th>
                    <th>Type</th>
                    <th>Total Amount</th>
                    <th>Amount Paid</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!salessales ? (
                    <tr>
                      <td
                        colSpan="10"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Loading Sales...
                      </td>
                    </tr>
                  ) : paginatedSales.length === 0 ? (
                    <tr>
                      <td
                        colSpan="10"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        No sales match your filters
                      </td>
                    </tr>
                  ) : (
                    paginatedSales.map((sale) => {
                      const balanceDisplay = getBalanceDisplay(sale);
                      const isOnline = isOnlineOrder(sale);
                      return (
                        <tr key={sale.id} style={styles.tableRow}>
                          <td style={styles.td}>
                            {sale.attributes.field_order_number}
                          </td>
                          <td style={styles.td}>
                            {new Date(
                              sale.attributes.created,
                            ).toLocaleDateString()}
                          </td>
                          <td style={styles.td}>{getCustomerName(sale)}</td>
                          <td style={styles.td}>
                            {getCustomerPhoneNumber(sale)}
                          </td>
                          <td style={styles.td}>{getCashierName(sale)}</td>
                          <td style={styles.td}>
                            <span
                              style={{
                                backgroundColor: isOnline ? '#e3f2fd' : '#fff3e0',
                                color: isOnline ? '#1976d2' : '#ed6c02',
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "600",
                                textTransform: "capitalize",
                              }}
                            >
                              <FontAwesomeIcon 
                                icon={isOnline ? faGlobe : faStore} 
                                style={{ marginRight: '4px' }}
                              />
                              {isOnline ? 'Online' : 'POS'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_amount,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_paid || 0,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                color: balanceDisplay.color,
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              {balanceDisplay.formatted}
                            </span>
                          </td>
                          <td style={styles.td}>
                             <div style={styles.actionBTN}>
                            <button
                              style={styles.viewButton}
                              onClick={() =>
                                stateNavigator.navigate(
                                  "viewsalesinvoicedetails",
                                  { salesId: sale.id },
                                )
                              }
                              title="View Invoice"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              style={styles.editButton}
                              onClick={() =>
                                stateNavigator.navigate("editsalesorder", {
                                  salesId: sale.id,
                                })
                              }
                              title="Edit Sale"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              style={styles.deleteButton}
                              onClick={() => handleDelete(sale.id)}
                              title="Delete Sale"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                <div style={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        style={{
                          ...styles.pageButton,
                          ...(currentPage === pageNum
                            ? styles.activePageButton
                            : {}),
                        }}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>

                <div style={styles.pageInfo}>
                  Page {currentPage} of {totalPages} ({filteredsales.length}{" "}
                  total items)
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message="Sale deleted successfully!"
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faFilter}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1100px",
  },
  tableRow: {
    borderBottom: "1px solid #dee2e6",
  },
  td: {
    padding: "14px",
    borderBottom: "1px solid #dee2e6",
    fontSize: "16px",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  statIcon: {
    fontSize: "24px",
    color: "#007bff",
  },
  statValue: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "15px",
  },
  statSubtext: {
    color: "#000000",
    fontSize: "20px",
    fontWeight: "600",
    display: "block",
    marginTop: "2px",
  },
  filterToggleBar: {
    display: "flex",
    gap: "10px",
    marginBottom: "15px",
    flexWrap: "wrap",
  },
  toggleFiltersButton: {
    background: "#007bff",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background 0.2s",
  },
  clearFiltersButton: {
    background: "#dc3545",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background 0.2s",
  },
  filtersContainer: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    marginBottom: "20px",
  },
  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "15px",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  filterLabel: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#555",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  filterIcon: {
    fontSize: "12px",
    color: "#007bff",
  },
  filterInput: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "15px",
    transition: "border-color 0.2s",
    outline: "none",
    width: "100%",
    height: "60px",
    boxSizing: "border-box",
  },
  viewButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    marginRight: "5px",
    width: "64px",
    height: "32px"
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    marginLeft: "5px",
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    marginRight: "5px",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: "30px",
  },
  paginationButton: {
    background: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    width: "94px",
    height: "64px"
  },
  pageNumbers: {
    display: "flex",
    gap: "12px",
  },
  pageButton: {
    background: "white",
    border: "1px solid #dee2e6",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    transition: "all 0.2s",
    minWidth: "40px",
    width: "64px",
    height: "64px"
  },
  activePageButton: {
    background: "#000000",
    color: "white",
    borderColor: "#000000",
        width: "64px",
    height: "64px"
  },
  pageInfo: {
    color: "#666",
    fontSize: "14px",
  },
    actionBTN: {
    display: "flex",
    gap: "12px",
    width: "64px",
    height: "32px"
  },
};